// GoldenHarvestLayout.js - Bakery Theme
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  StoreLogo, CartBtn, TabContent, StoreFooter, 
  AWS_URL, FaHeart
} from './ThemeLayoutHelpers';

// Custom InlineEditableTextWithNav component
const InlineEditableTextWithNav = ({ p, field, value, className = "", style = {}, children, onNavigate, navPage, isInModal = false }) => {
  const isEditing = p.previewEditMode && p.activeEditField === field;
  const isEditable = typeof p.onActivateField === "function";
  
  const getCurrentValue = () => {
    if (p.storeTheme && p.storeTheme[field] !== undefined && p.storeTheme[field] !== "") {
      return p.storeTheme[field];
    }
    return value;
  };
  
  const [localValue, setLocalValue] = useState(getCurrentValue());

  useEffect(() => {
    if (!isEditing) {
      setLocalValue(getCurrentValue());
    }
  }, [p.storeTheme, field, value, isEditing]);

  const handleActivate = (event) => {
    event.preventDefault();
    event.stopPropagation();
    const currentVal = getCurrentValue();
    setLocalValue(currentVal);
    if (typeof p.onActivateField === "function") {
      p.onActivateField(field);
    }
  };

  const handleChange = (event) => {
    const newValue = event.target.value;
    setLocalValue(newValue);
  };

  const handleSaveAndClose = () => {
    if (typeof p.onPreviewTextChange === "function") {
      p.onPreviewTextChange(field, localValue);
    }
    if (typeof p.onDeactivateField === "function") {
      p.onDeactivateField();
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
      handleSaveAndClose();
      if (!isInModal && onNavigate && navPage) {
        onNavigate(navPage);
      }
    }
  };

  const handleBlur = () => {
    handleSaveAndClose();
  };

  if (isEditing) {
    const inputClass = "w-full rounded-2xl border border-amber-200 bg-white/90 px-3 py-2 text-sm text-gray-900 outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-100 dark:border-amber-700 dark:bg-gray-800 dark:text-gray-100";
    const multiline = field.toLowerCase().includes("subtitle") || field.toLowerCase().includes("description") || field.toLowerCase().includes("overview") || field.toLowerCase().includes("content");

    return multiline ? (
      <textarea
        autoFocus
        rows={4}
        value={localValue}
        onChange={handleChange}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        onClick={(event) => event.stopPropagation()}
        className={inputClass}
        style={{ whiteSpace: "pre-wrap", ...style }}
      />
    ) : (
      <input
        autoFocus
        type="text"
        value={localValue}
        onChange={handleChange}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        onClick={(event) => event.stopPropagation()}
        className={inputClass}
        style={{ whiteSpace: "pre-wrap", ...style }}
      />
    );
  }

  const displayValue = p.storeTheme?.[field] || value;
  
  return (
    <span
      className={`${className} ${isEditable ? 'cursor-pointer inline-block' : 'inline-block'}`}
      style={{ whiteSpace: "pre-wrap", ...style }}
      onClick={isEditable ? handleActivate : undefined}
      title={p.previewEditMode ? "Edit text" : undefined}
    >
      {displayValue}
    </span>
  );
};

export default function GoldenHarvestLayout({ p }) {
  const T = p.storeTheme || {};
  const primary = T.primaryColor || "#b45309";
  const secondary = T.secondaryColor || "#d97706";
  
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeSlide, setActiveSlide] = useState(0);
  const [visibleProducts, setVisibleProducts] = useState([]);
  const [activePage, setActivePage] = useState("home");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedPost, setSelectedPost] = useState(null);
  const [selectedRecipe, setSelectedRecipe] = useState(null);
  const [hoveredCard, setHoveredCard] = useState(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [animationComplete, setAnimationComplete] = useState(false);
  
  const heroRef = useRef(null);
  const [refreshKey, setRefreshKey] = useState(0);
  
  useEffect(() => {
    setRefreshKey(prev => prev + 1);
  }, [p.storeTheme]);

  // Bakery hero images
  const bakeryHeroImages = [
    "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/1dfc2629-f789-4ef0-bb07-dc4851ac683b_b1.jpg",
    "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/5fab19fd-af3c-4661-b34b-5a4ab7075995_b2.jpg",
    "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/81511a48-fd45-4e66-9875-e04e05668977_b3.jpg",
    "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/cec8b2b3-4515-4787-b451-8097794c5de4_b4.jpg",
    "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/a6b04983-cc99-4f59-b115-a076a60e8461_b5.jpg",
  ];

  // Bakery Collection Data
  const bakeryCollection = [
    {
      id: 1,
      nameKey: "item1_name",
      name: "Chocolate Fudge Cake",
      priceKey: "item1_price",
      price: 899,
      originalPriceKey: "item1_original_price",
      originalPrice: 1299,
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/03076b65-ef4e-404a-9604-93f1b7354170_b6.jpg",
      categoryKey: "item1_category",
      category: "Cakes",
      descriptionKey: "item1_description",
      description: "Rich chocolate fudge cake with layers of dark chocolate ganache.",
      badge: "Bestseller",
      badgeColor: "#b45309",
      serves: "8-10 people",
      ingredients: ["Dark Chocolate", "Vanilla", "Eggs", "Flour"],
      isVeg: true
    },
    {
      id: 2,
      nameKey: "item2_name",
      name: "Croissant Platter",
      priceKey: "item2_price",
      price: 499,
      originalPriceKey: "item2_original_price",
      originalPrice: 699,
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/941833b5-ef10-4116-8338-33b81ee858b3_b7.jpg",
      categoryKey: "item2_category",
      category: "Pastries",
      descriptionKey: "item2_description",
      description: "Buttery, flaky croissants baked fresh every morning.",
      badge: "Fresh",
      badgeColor: "#059669",
      serves: "6 pieces",
      ingredients: ["Butter", "Flour", "Yeast", "Milk"],
      isVeg: true
    },
    {
      id: 3,
      nameKey: "item3_name",
      name: "Red Velvet Cake",
      priceKey: "item3_price",
      price: 999,
      originalPriceKey: "item3_original_price",
      originalPrice: 1499,
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/5bf9bf43-6af2-40cd-93e3-9e7c555cffca_b8.jpg",
      categoryKey: "item3_category",
      category: "Cakes",
      descriptionKey: "item3_description",
      description: "Classic red velvet with cream cheese frosting.",
      badge: "Premium",
      badgeColor: "#dc2626",
      serves: "10-12 people",
      ingredients: ["Buttermilk", "Cocoa", "Cream Cheese", "Vanilla"],
      isVeg: true
    },
    {
      id: 4,
      nameKey: "item4_name",
      name: "Danish Pastry Box",
      priceKey: "item4_price",
      price: 649,
      originalPriceKey: "item4_original_price",
      originalPrice: 899,
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/26d2fd3e-7510-47ba-bf14-3a7a0b4a90aa_b9.jpg",
      categoryKey: "item4_category",
      category: "Pastries",
      descriptionKey: "item4_description",
      description: "Assorted Danish pastries with fruit and cream fillings.",
      badge: "Popular",
      badgeColor: "#f59e0b",
      serves: "9 pieces",
      ingredients: ["Butter", "Flour", "Fruit", "Cream"],
      isVeg: true
    },
    {
      id: 5,
      nameKey: "item5_name",
      name: "Tiramisu",
      priceKey: "item5_price",
      price: 749,
      originalPriceKey: "item5_original_price",
      originalPrice: 999,
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/aa2a338a-6d7d-45af-aa9b-cb9b01bfcac6_Tiramisu.jpg",
      categoryKey: "item5_category",
      category: "Desserts",
      descriptionKey: "item5_description",
      description: "Classic Italian tiramisu with mascarpone and coffee.",
      badge: "Signature",
      badgeColor: "#7c3aed",
      serves: "6-8 people",
      ingredients: ["Mascarpone", "Espresso", "Ladyfingers", "Cocoa"],
      isVeg: true
    },
    {
      id: 6,
      nameKey: "item6_name",
      name: "Sourdough Bread",
      priceKey: "item6_price",
      price: 399,
      originalPriceKey: "item6_original_price",
      originalPrice: 549,
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/3078a531-8f52-4393-8f2b-2ca974f259cd_Sourdough_Bread.jpg",
      categoryKey: "item6_category",
      category: "Breads",
      descriptionKey: "item6_description",
      description: "Artisan sourdough with a crispy crust and airy crumb.",
      badge: "Artisan",
      badgeColor: "#8b5cf6",
      serves: "1 loaf",
      ingredients: ["Flour", "Water", "Salt", "Starter"],
      isVeg: true
    },
    {
      id: 7,
      nameKey: "item7_name",
      name: "Cupcake Collection",
      priceKey: "item7_price",
      price: 449,
      originalPriceKey: "item7_original_price",
      originalPrice: 649,
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/c45ea586-a304-43db-9b2c-9d048ea26fe0_Cupcake_Collection.jpg",
      categoryKey: "item7_category",
      category: "Cakes",
      descriptionKey: "item7_description",
      description: "Assorted gourmet cupcakes with various toppings.",
      badge: "Party Pack",
      badgeColor: "#ec4899",
      serves: "12 cupcakes",
      ingredients: ["Flour", "Sugar", "Butter", "Eggs"],
      isVeg: true
    },
    {
      id: 8,
      nameKey: "item8_name",
      name: "Baguette & Butter",
      priceKey: "item8_price",
      price: 299,
      originalPriceKey: "item8_original_price",
      originalPrice: 449,
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/358b40c3-0c2b-427a-9e28-c6bcd750eb9b_Baguette_&_Butter.jpg",
      categoryKey: "item8_category",
      category: "Breads",
      descriptionKey: "item8_description",
      description: "Freshly baked baguette with French butter.",
      badge: "Classic",
      badgeColor: "#14b8a6",
      serves: "1 baguette",
      ingredients: ["Flour", "Yeast", "Salt", "Water"],
      isVeg: true
    }
  ];

  // Recipes
  const recipes = [
    {
      id: 1,
      titleKey: "recipe1_title",
      title: "Classic Sourdough",
      descKey: "recipe1_desc",
      desc: "Master the art of sourdough baking with our step-by-step guide.",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/caae2653-33b4-4745-b201-536f103c60ed_Classic_Sourdough.jpg",
      difficulty: "Intermediate",
      time: "24 hours",
      ingredients: ["Flour", "Water", "Salt", "Sourdough Starter"],
      steps: [
        "Mix flour and water, autolyse for 1 hour",
        "Add starter and salt, knead until smooth",
        "Bulk ferment with stretch and folds",
        "Shape and proof in basket",
        "Score and bake in Dutch oven"
      ]
    },
    {
      id: 2,
      titleKey: "recipe2_title",
      title: "Perfect Croissants",
      descKey: "recipe2_desc",
      desc: "Buttery, flaky croissants that rival Parisian bakeries.",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/81511a48-fd45-4e66-9875-e04e05668977_b3.jpg",
      difficulty: "Advanced",
      time: "8 hours",
      ingredients: ["Butter", "Flour", "Yeast", "Milk", "Sugar", "Salt"],
      steps: [
        "Make dough and chill",
        "Create butter block",
        "Laminate dough with butter",
        "Roll, cut, and shape",
        "Proof and bake golden"
      ]
    },
    {
      id: 3,
      titleKey: "recipe3_title",
      title: "Fudgy Brownies",
      descKey: "recipe3_desc",
      desc: "Ultimate fudgy brownies with a crackly top.",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/1e411970-78ab-4815-9685-c4393e231fd9_Fudgy_Brownies.jpg",
      difficulty: "Beginner",
      time: "1 hour",
      ingredients: ["Chocolate", "Butter", "Sugar", "Eggs", "Flour", "Cocoa"],
      steps: [
        "Melt chocolate and butter together",
        "Whisk in sugar and eggs",
        "Fold in dry ingredients",
        "Pour into prepared pan",
        "Bake until set but fudgy"
      ]
    }
  ];

  // Blog Posts
  const editorialPosts = [
    {
      id: 1,
      titleKey: "post1_title",
      title: "The Art of Sourdough",
      descKey: "post1_desc",
      desc: "Discover the secrets of perfect sourdough bread.",
      fullContentKey: "post1_content",
      fullContent: "Sourdough is more than just bread—it's a living tradition. Our master baker shares the techniques that create that perfect crust and airy crumb. From maintaining your starter to achieving the perfect ear, we cover it all.",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/98168474-701d-4e06-88c8-51fbf086d38a_The_Art_of_Sourdough.jpg",
      dateKey: "post1_date",
      date: "March 15, 2024",
      authorKey: "post1_author",
      author: "Marie Laurent",
      category: "Bread Making"
    },
    {
      id: 2,
      titleKey: "post2_title",
      title: "The Chemistry of Cakes",
      descKey: "post2_desc",
      desc: "Understanding the science behind perfect cakes.",
      fullContentKey: "post2_content",
      fullContent: "Baking is a science—every ingredient has a purpose. Learn how different techniques affect the texture, rise, and flavor of your cakes, from creaming method to folding egg whites.",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/40131799-1333-40b1-b8ee-80e2102033a2_cake.jpg",
      dateKey: "post2_date",
      date: "March 10, 2024",
      authorKey: "post2_author",
      author: "James Baker",
      category: "Cake Science"
    },
    {
      id: 3,
      titleKey: "post3_title",
      title: "Artisan Pastry Techniques",
      descKey: "post3_desc",
      desc: "Master the delicate art of French pastry making.",
      fullContentKey: "post3_content",
      fullContent: "French pastry is the pinnacle of baking artistry. From laminating dough to creating the perfect choux, our experts reveal the techniques behind world-class pastries.",
      image: "https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/2fc85662-446b-40ae-b8d5-b777805a9d26_Artisan_Pastry_Techniques.jpg",
      dateKey: "post3_date",
      date: "March 5, 2024",
      authorKey: "post3_author",
      author: "Sophie Dubois",
      category: "Pastry Arts"
    }
  ];

  // Navigation items
  const navItems = [
    { key: "nav_home", label: "HOME", page: "home" },
    { key: "nav_cakes", label: "CAKES", page: "cakes" },
    { key: "nav_pastries", label: "PASTRIES", page: "pastries" },
    { key: "nav_breads", label: "BREADS", page: "breads" },
    { key: "nav_recipes", label: "RECIPES", page: "recipes" },
    { key: "nav_journal", label: "JOURNAL", page: "journal" },
    { key: "nav_about", label: "ABOUT", page: "about" },
  ];

  // Bakery Categories
  const bakeryCategories = [
    { key: "cat_cakes", icon: "🎂", label: "Cakes", color: "#dc2626", bgColor: "#fef2f2" },
    { key: "cat_pastries", icon: "🥐", label: "Pastries", color: "#b45309", bgColor: "#fffbeb" },
    { key: "cat_breads", icon: "🍞", label: "Breads", color: "#8b5cf6", bgColor: "#f5f3ff" },
    { key: "cat_desserts", icon: "🍰", label: "Desserts", color: "#ec4899", bgColor: "#fdf2f8" },
  ];

  // Special Features
  const specialFeatures = [
    { key: "feature1", icon: "🌾", title: "Sourdough Starter", desc: "Live culture available daily", color: "#b45309" },
    { key: "feature2", icon: "🥚", title: "Cage-Free Eggs", desc: "Ethically sourced ingredients", color: "#059669" },
    { key: "feature3", icon: "🧈", title: "French Butter", desc: "Imported from Normandy", color: "#d97706" },
    { key: "feature4", icon: "🍫", title: "Single-Origin Chocolate", desc: "Premium cocoa beans", color: "#7c3aed" },
  ];

  // Top bar messages
  const topBarMessages = [
    "🍞 FRESH BAKED DAILY",
    "✦ FREE DELIVERY ON FIRST ORDER",
    "🥐 ARTISAN PASTRIES",
    "✦ 30-DAY SATISFACTION GUARANTEE"
  ];

  // Floating particles (wheat/aroma)
  const particles = Array.from({ length: 25 }, (_, i) => ({
    id: i,
    left: Math.random() * 100,
    top: Math.random() * 100,
    size: 2 + Math.random() * 6,
    delay: Math.random() * 5,
    duration: 5 + Math.random() * 5,
    opacity: 0.1 + Math.random() * 0.25,
  }));

  // Auto-rotate hero images
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveSlide((prev) => (prev + 1) % bakeryHeroImages.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  // Animate products
  useEffect(() => {
    const products = p.filteredProducts || [];
    if (products.length === 0) return;
    
    setTimeout(() => setAnimationComplete(true), 300);
    
    products.forEach((_, index) => {
      setTimeout(() => {
        setVisibleProducts(prev => [...prev, index]);
      }, index * 100 + 500);
    });
  }, [p.filteredProducts]);

  // Mouse move for parallax
  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({
        x: (e.clientX / window.innerWidth - 0.5) * 15,
        y: (e.clientY / window.innerHeight - 0.5) * 10,
      });
    };
    window.addEventListener("mouseup", handleMouseMove);
    return () => window.removeEventListener("mouseup", handleMouseMove);
  }, []);

  // Scroll handler
  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navigateTo = useCallback((page, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (p.previewEditMode && p.activeEditField) {
      return;
    }
    setActivePage(page);
    setMobileMenuOpen(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [p.previewEditMode, p.activeEditField]);

  const handleNavigateAfterEdit = useCallback((page) => {
    setActivePage(page);
    setMobileMenuOpen(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const openBlogModal = useCallback((post, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (p.previewEditMode && p.activeEditField) {
      return;
    }
    setSelectedPost(post);
  }, [p.previewEditMode, p.activeEditField]);

  const closeBlogModal = useCallback(() => {
    setSelectedPost(null);
  }, []);

  const openRecipeModal = useCallback((recipe, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (p.previewEditMode && p.activeEditField) {
      return;
    }
    setSelectedRecipe(recipe);
  }, [p.previewEditMode, p.activeEditField]);

  const closeRecipeModal = useCallback(() => {
    setSelectedRecipe(null);
  }, []);

  const handleBackdropClick = useCallback((event) => {
    if (event.target === event.currentTarget) {
      if (p.previewEditMode && p.activeEditField) {
        return;
      }
      setSelectedItem(null);
      setSelectedPost(null);
      setSelectedRecipe(null);
    }
  }, [p.previewEditMode, p.activeEditField]);

  const copy = {
    eyebrow: p.storeTheme?.heroEyebrow || "GOLDEN HARVEST",
    title: p.storeTheme?.heroTitle || "Artisan Bakery & Patisserie",
    subtitle: p.storeTheme?.heroSubtitle || "Freshly baked goods made with love and the finest ingredients",
    primaryCta: p.storeTheme?.primaryCtaText || "Order Now",
    secondaryCta: p.storeTheme?.secondaryCtaText || "View Menu",
  };

  const isDarkTheme = (color) => {
    if (!color) return false;
    const hex = color.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance < 0.5;
  };

  const isDark = isDarkTheme(T.backgroundColor);

  // Get card entrance animation
  const getCardEntrance = (index) => {
    const directions = [
      { x: -60, y: -20, rotate: -4 },
      { x: 60, y: -20, rotate: 4 },
      { x: -60, y: 20, rotate: -3 },
      { x: 60, y: 20, rotate: 3 },
    ];
    return directions[index % directions.length];
  };

  return (
    <div key={refreshKey} className="w-full min-h-screen bg-amber-50 overflow-x-hidden" style={{ fontFamily: T.fontFamily || "'DM Sans', sans-serif" }}>
      
      {/* Floating Aroma/Wheat Particles */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden z-0">
        {particles.map((particle) => (
          <div
            key={particle.id}
            className="absolute rounded-full animate-float-wheat"
            style={{
              left: `${particle.left}%`,
              top: `${particle.top}%`,
              width: `${particle.size}px`,
              height: `${particle.size}px`,
              background: `radial-gradient(circle, ${primary}${Math.floor(20 + Math.random() * 40).toString(16)}, transparent)`,
              animationDelay: `${particle.delay}s`,
              animationDuration: `${particle.duration}s`,
              opacity: particle.opacity,
            }}
          />
        ))}
      </div>
      
      {/* Top Bar */}
      <div className="relative bg-amber-800 text-amber-200 py-2 overflow-hidden">
        <div className="whitespace-nowrap animate-marquee">
          {topBarMessages.map((msg, idx) => (
            <span key={idx} className="inline-block mx-8 text-xs tracking-wider">
              <InlineEditableTextWithNav 
                p={p} 
                field={`top_bar_message_${idx + 1}`} 
                value={msg}
                onNavigate={handleNavigateAfterEdit}
                navPage="home"
              />
            </span>
          ))}
        </div>
      </div>
      
      {/* Navigation - Warm Brown */}
      <nav className={`sticky top-0 z-50 transition-all duration-500 ${isScrolled ? "bg-amber-800/95 backdrop-blur-xl shadow-lg py-2" : "bg-amber-800/80 backdrop-blur-sm py-4"}`}>
        <div className="w-full px-4 md:px-8 flex items-center justify-between">
          <div className="flex items-center gap-3 cursor-pointer" onClick={(e) => navigateTo("home", e)}>
            <StoreLogo storeInfo={p.storeInfo} border={`border-2 ${isScrolled ? "border-amber-400" : "border-amber-300"}`} />
            <span className="font-bold text-xl tracking-tight text-amber-100">
              <InlineEditableTextWithNav 
                p={p} 
                field="businessName" 
                value={p.storeInfo?.businessName || "Golden Harvest"} 
                onNavigate={handleNavigateAfterEdit}
                navPage="home"
              />
            </span>
          </div>
          
          <button 
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (!(p.previewEditMode && p.activeEditField)) {
                setMobileMenuOpen(!mobileMenuOpen);
              }
            }} 
            className="md:hidden p-2"
          >
            <svg className="w-6 h-6 text-amber-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          
          <div className="hidden md:flex items-center gap-6">
            {navItems.map((item) => (
              <button
                key={item.key}
                onClick={(e) => navigateTo(item.page, e)}
                className={`text-sm font-medium transition-all duration-300 relative group ${activePage === item.page ? "text-amber-300" : "text-amber-100/80 hover:text-amber-200"}`}
              >
                <InlineEditableTextWithNav 
                  p={p} 
                  field={`nav_${item.page}`} 
                  value={item.label}
                  onNavigate={handleNavigateAfterEdit}
                  navPage={item.page}
                />
                <span className={`absolute -bottom-1 left-0 h-0.5 bg-amber-400 transition-all duration-300 ${activePage === item.page ? "w-full" : "w-0 group-hover:w-full"}`} />
              </button>
            ))}
          </div>
          
          <div className="flex items-center gap-3">
            <button 
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (!(p.previewEditMode && p.activeEditField)) {
                  p.handleGoToWishlist?.();
                }
              }} 
              className="relative group"
            >
              <div className="p-2 rounded-full transition-all duration-300 hover:bg-amber-700/50">
                <FaHeart className="text-lg text-amber-200 hover:text-amber-100 transition-colors" />
              </div>
              {p.wishlistCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-amber-500 text-white text-[10px] flex items-center justify-center font-bold animate-pulse">
                  {p.wishlistCount}
                </span>
              )}
            </button>
            <CartBtn p={p} dark={true} />
            {p.userMenuElement}
          </div>
        </div>
        
        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden absolute top-full left-0 right-0 bg-amber-800 shadow-lg py-4 px-4 flex flex-col gap-2 z-50 border-t border-amber-700">
            {navItems.map((item) => (
              <button
                key={item.key}
                onClick={(e) => navigateTo(item.page, e)}
                className={`text-sm py-2 px-3 rounded-lg text-left ${activePage === item.page ? "bg-amber-700/50 text-amber-200" : "text-amber-100/80 hover:text-amber-200"}`}
              >
                <InlineEditableTextWithNav 
                  p={p} 
                  field={`nav_${item.page}`} 
                  value={item.label}
                  onNavigate={handleNavigateAfterEdit}
                  navPage={item.page}
                />
              </button>
            ))}
          </div>
        )}
      </nav>
      
      {/* ==================== HOME PAGE ==================== */}
      {activePage === "home" && (
        <>
          {/* Hero Section */}
          <section className="relative w-full h-screen min-h-[700px] overflow-hidden -mt-[60px]">
            <div className="absolute inset-0 w-full">
              {bakeryHeroImages.map((img, idx) => (
                <div 
                  key={idx} 
                  className={`absolute inset-0 w-full transition-opacity duration-1000 ease-in-out ${idx === activeSlide ? "opacity-100" : "opacity-0"}`}
                >
                  <img 
                    src={img} 
                    alt={`Bakery ${idx + 1}`} 
                    className="w-full h-full object-cover object-center" 
                    style={{ 
                      transform: `translate(${mousePosition.x * 0.3}px, ${mousePosition.y * 0.3}px) scale(1.05)`, 
                      transition: "transform 0.3s ease-out" 
                    }} 
                  />
                </div>
              ))}
              <div className="absolute inset-0 bg-gradient-to-r from-amber-900/80 via-amber-900/40 to-transparent" />
              <div className="absolute inset-0 bg-gradient-to-t from-amber-50 via-transparent to-transparent" />
            </div>
            
            <div className="relative h-full w-full px-4 md:px-8 flex items-center">
              <div className="max-w-3xl">
                <div className="inline-block mb-6 animate-bounce-in">
                  <div className="flex items-center gap-3 px-5 py-2.5 bg-amber-800/30 backdrop-blur-md rounded-full border border-amber-400/30">
                    <span className="w-2 h-2 rounded-full bg-amber-400 animate-ping" />
                    <span className="text-amber-200 text-xs tracking-[0.2em] uppercase">
                      <InlineEditableTextWithNav 
                        p={p} 
                        field="heroEyebrow" 
                        value={copy.eyebrow}
                        onNavigate={handleNavigateAfterEdit}
                        navPage="home"
                      />
                    </span>
                    <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
                  </div>
                </div>
                <h1 className="text-5xl md:text-7xl lg:text-8xl font-bold text-white leading-[1.05] tracking-tight">
                  <InlineEditableTextWithNav 
                    p={p} 
                    field="heroTitle" 
                    value={copy.title}
                    onNavigate={handleNavigateAfterEdit}
                    navPage="home"
                  >
                    {copy.title.split(" ").map((word, i) => (
                      <span key={i} className="inline-block animate-word-pop" style={{ animationDelay: `${i * 0.15}s` }}>{word}{" "}</span>
                    ))}
                  </InlineEditableTextWithNav>
                </h1>
                <p className="mt-6 text-amber-100/90 text-lg max-w-xl animate-slide-right">
                  <InlineEditableTextWithNav 
                    p={p} 
                    field="heroSubtitle" 
                    value={copy.subtitle}
                    onNavigate={handleNavigateAfterEdit}
                    navPage="home"
                  />
                </p>
                <div className="mt-10 flex flex-wrap gap-4">
                  <button 
                    onClick={(e) => navigateTo("cakes", e)} 
                    className="group px-8 py-4 rounded-full bg-amber-600 text-white font-semibold tracking-wide transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-amber-600/30 flex items-center gap-2 animate-slide-up"
                  >
                    <InlineEditableTextWithNav 
                      p={p} 
                      field="primaryCtaText" 
                      value={copy.primaryCta}
                      onNavigate={handleNavigateAfterEdit}
                      navPage="cakes"
                    />
                    <span className="group-hover:translate-x-1 transition-transform">→</span>
                  </button>
                  <button 
                    onClick={(e) => navigateTo("pastries", e)} 
                    className="px-8 py-4 rounded-full border-2 border-amber-400/50 text-amber-200 font-semibold tracking-wide transition-all duration-300 hover:bg-amber-600/30 hover:border-amber-300 backdrop-blur-sm animate-slide-up"
                  >
                    <InlineEditableTextWithNav 
                      p={p} 
                      field="secondaryCtaText" 
                      value={copy.secondaryCta}
                      onNavigate={handleNavigateAfterEdit}
                      navPage="pastries"
                    />
                  </button>
                </div>
              </div>
            </div>
            
            <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce-slow">
              <div className="flex flex-col items-center gap-1">
                <span className="text-amber-200/60 text-[10px] tracking-wider">
                  <InlineEditableTextWithNav 
                    p={p} 
                    field="scroll_text" 
                    value="SCROLL TO EXPLORE"
                    onNavigate={handleNavigateAfterEdit}
                    navPage="home"
                  />
                </span>
                <div className="w-5 h-8 border-2 border-amber-400/30 rounded-full flex justify-center">
                  <div className="w-1 h-2 bg-amber-400/60 rounded-full mt-1 animate-scroll-dot" />
                </div>
              </div>
            </div>
          </section>
          
          {/* Featured Badge */}
          <div className="relative z-10 -mt-8 text-center">
            <div className="inline-flex items-center gap-3 px-6 py-3 bg-white rounded-full shadow-lg border border-amber-100">
              <span className="text-amber-600 text-sm">🥐</span>
              <span className="text-amber-900 text-xs font-semibold tracking-wider">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="collection_badge" 
                  value="FRESH FROM THE OVEN"
                  onNavigate={handleNavigateAfterEdit}
                  navPage="home"
                />
              </span>
              <span className="text-amber-600 text-sm">🍞</span>
            </div>
          </div>
          
          {/* Bakery Categories */}
          <section className="w-full px-4 md:px-8 py-12">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {bakeryCategories.map((cat, idx) => (
                <div 
                  key={cat.key} 
                  className="group cursor-pointer animate-card-fly" 
                  style={{ animationDelay: `${idx * 0.12}s` }}
                  onClick={(e) => {
                    const pageMap = {
                      cat_cakes: "cakes",
                      cat_pastries: "pastries",
                      cat_breads: "breads",
                      cat_desserts: "pastries"
                    };
                    navigateTo(pageMap[cat.key] || "home", e);
                  }}
                >
                  <div 
                    className="relative overflow-hidden rounded-2xl p-6 text-center transition-all duration-500 hover:-translate-y-2 hover:shadow-xl"
                    style={{ backgroundColor: cat.bgColor }}
                  >
                    <div className="text-5xl mb-3 transition-all duration-300 group-hover:scale-110 group-hover:rotate-6">
                      {cat.icon}
                    </div>
                    <h3 className="font-bold" style={{ color: cat.color }}>
                      <InlineEditableTextWithNav 
                        p={p} 
                        field={cat.key} 
                        value={cat.label}
                        onNavigate={handleNavigateAfterEdit}
                        navPage="home"
                      />
                    </h3>
                    <div className="mt-2 h-0.5 w-0 group-hover:w-full transition-all duration-500 mx-auto" style={{ backgroundColor: cat.color }} />
                  </div>
                </div>
              ))}
            </div>
          </section>
          
          {/* Special Features */}
          <section className="w-full px-4 md:px-8 py-8 bg-white">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {specialFeatures.map((feature, idx) => (
                <div 
                  key={feature.key} 
                  className="group animate-card-fly" 
                  style={{ animationDelay: `${idx * 0.1}s` }}
                >
                  <div className="bg-amber-50 rounded-2xl p-6 text-center transition-all duration-300 hover:shadow-lg hover:-translate-y-1">
                    <div className="text-4xl mb-3">{feature.icon}</div>
                    <h4 className="font-bold text-amber-900 text-sm">
                      <InlineEditableTextWithNav 
                        p={p} 
                        field={feature.key} 
                        value={feature.title}
                        onNavigate={handleNavigateAfterEdit}
                        navPage="home"
                      />
                    </h4>
                    <p className="text-xs text-gray-500 mt-1">
                      <InlineEditableTextWithNav 
                        p={p} 
                        field={`${feature.key}_desc`} 
                        value={feature.desc}
                        onNavigate={handleNavigateAfterEdit}
                        navPage="home"
                      />
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </section>
          
          {/* Featured Items */}
          <section className="w-full px-4 md:px-8 py-8">
            <div className="text-center mb-10">
              <span className="text-amber-700 text-sm tracking-[0.2em] uppercase">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="featured_badge" 
                  value="Our Specialties"
                  onNavigate={handleNavigateAfterEdit}
                  navPage="home"
                />
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-amber-900 mt-2">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="featured_title" 
                  value="Fresh From The Oven"
                  onNavigate={handleNavigateAfterEdit}
                  navPage="home"
                />
              </h2>
              <div className="w-24 h-1 bg-gradient-to-r from-amber-600 to-amber-400 mx-auto mt-4 rounded-full" />
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {bakeryCollection.slice(0, 4).map((item, idx) => {
                const entrance = getCardEntrance(idx);
                const isVisible = animationComplete;
                return (
                  <div 
                    key={item.id} 
                    className={`group cursor-pointer transition-all duration-700 ${isVisible ? "opacity-100 translate-x-0 translate-y-0 rotate-0" : `opacity-0 translate-x-[${entrance.x}px] translate-y-[${entrance.y}px] rotate-[${entrance.rotate}deg]`}`}
                    onMouseEnter={() => setHoveredCard(idx)}
                    onMouseLeave={() => setHoveredCard(null)}
                    onClick={() => setSelectedItem(item)}
                  >
                    <div className="relative overflow-hidden rounded-2xl bg-white shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-3">
                      <div className="relative aspect-square overflow-hidden bg-amber-50">
                        <img src={item.image} alt={item.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                        {item.badge && (
                          <div className="absolute top-3 left-3 px-3 py-1 rounded-full text-white text-xs font-bold shadow-lg" style={{ backgroundColor: item.badgeColor }}>
                            <InlineEditableTextWithNav 
                              p={p} 
                              field={`${item.nameKey}_badge`} 
                              value={item.badge}
                              onNavigate={handleNavigateAfterEdit}
                              navPage="home"
                            />
                          </div>
                        )}
                        {item.isVeg && (
                          <div className="absolute top-3 right-3 bg-green-500 text-white text-xs font-bold px-2 py-1 rounded-full">
                            🟢 VEG
                          </div>
                        )}
                      </div>
                      <div className="p-4">
                        <h3 className="font-semibold text-gray-800 text-sm line-clamp-1">
                          <InlineEditableTextWithNav 
                            p={p} 
                            field={item.nameKey} 
                            value={item.name}
                            onNavigate={handleNavigateAfterEdit}
                            navPage="home"
                          />
                        </h3>
                        <div className="mt-2 flex items-center justify-center gap-2">
                          <span className="text-xl font-bold text-amber-700">₹<InlineEditableTextWithNav 
                            p={p} 
                            field={item.priceKey} 
                            value={item.price.toString()}
                            onNavigate={handleNavigateAfterEdit}
                            navPage="home"
                          /></span>
                          {item.originalPrice > item.price && (
                            <span className="text-sm text-gray-400 line-through">₹<InlineEditableTextWithNav 
                              p={p} 
                              field={item.originalPriceKey} 
                              value={item.originalPrice.toString()}
                              onNavigate={handleNavigateAfterEdit}
                              navPage="home"
                            /></span>
                          )}
                        </div>
                        <p className="text-xs text-gray-500 mt-1 line-clamp-2">
                          <InlineEditableTextWithNav 
                            p={p} 
                            field={item.descriptionKey} 
                            value={item.description}
                            onNavigate={handleNavigateAfterEdit}
                            navPage="home"
                          />
                        </p>
                        <div className="mt-2 flex justify-center gap-1">
                          <span className="text-xs bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full">
                            {item.category}
                          </span>
                          <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">
                            {item.serves}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            
            <div className="text-center mt-10">
              <button 
                onClick={(e) => navigateTo("cakes", e)} 
                className="px-8 py-3 rounded-full bg-amber-700 text-white font-semibold hover:bg-amber-800 transition-all duration-300 hover:scale-105"
              >
                <InlineEditableTextWithNav 
                  p={p} 
                  field="view_all_button" 
                  value="View Full Menu"
                  onNavigate={handleNavigateAfterEdit}
                  navPage="cakes"
                />
              </button>
            </div>
          </section>
          
          {/* Recipe Preview */}
          <section className="w-full px-4 md:px-8 py-12 bg-white">
            <div className="text-center mb-10">
              <span className="text-amber-700 text-sm tracking-[0.2em] uppercase">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="recipes_badge" 
                  value="Baking Tips"
                  onNavigate={handleNavigateAfterEdit}
                  navPage="recipes"
                />
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-amber-900 mt-2">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="recipes_title" 
                  value="Our Favorite Recipes"
                  onNavigate={handleNavigateAfterEdit}
                  navPage="recipes"
                />
              </h2>
              <div className="w-24 h-1 bg-gradient-to-r from-amber-600 to-amber-400 mx-auto mt-4 rounded-full" />
            </div>
            
            <div className="grid md:grid-cols-3 gap-6">
              {recipes.map((recipe) => (
                <div 
                  key={recipe.id} 
                  className="group cursor-pointer bg-amber-50 rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2"
                  onClick={(e) => openRecipeModal(recipe, e)}
                >
                  <div className="h-48 overflow-hidden">
                    <img src={recipe.image} alt={recipe.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                  </div>
                  <div className="p-5">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xs bg-amber-200 text-amber-800 px-2 py-1 rounded-full font-semibold">
                        {recipe.difficulty}
                      </span>
                      <span className="text-xs text-gray-500">⏱ {recipe.time}</span>
                    </div>
                    <h3 className="text-lg font-bold text-amber-900 mb-1">
                      <InlineEditableTextWithNav 
                        p={p} 
                        field={recipe.titleKey} 
                        value={recipe.title}
                        onNavigate={handleNavigateAfterEdit}
                        navPage="recipes"
                      />
                    </h3>
                    <p className="text-gray-600 text-sm">
                      <InlineEditableTextWithNav 
                        p={p} 
                        field={recipe.descKey} 
                        value={recipe.desc}
                        onNavigate={handleNavigateAfterEdit}
                        navPage="recipes"
                      />
                    </p>
                    <div className="mt-3 text-amber-700 text-sm font-semibold flex items-center gap-1 group-hover:gap-2 transition-all">
                      <InlineEditableTextWithNav 
                        p={p} 
                        field="view_recipe_button" 
                        value="View Recipe"
                        onNavigate={handleNavigateAfterEdit}
                        navPage="recipes"
                      />
                      <span>→</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        </>
      )}
      
      {/* ==================== CAKES PAGE ==================== */}
      {activePage === "cakes" && (
        <section className="w-full px-4 md:px-8 py-12">
          <div className="text-center mb-12">
            <span className="text-rose-700 text-sm tracking-[0.2em] uppercase">
              <InlineEditableTextWithNav 
                p={p} 
                field="cakes_badge" 
                value="Our Collection"
                onNavigate={handleNavigateAfterEdit}
                navPage="cakes"
              />
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-rose-900 mt-2">
              <InlineEditableTextWithNav 
                p={p} 
                field="cakes_title" 
                value="Artisan Cakes"
                onNavigate={handleNavigateAfterEdit}
                navPage="cakes"
              />
            </h2>
            <p className="text-gray-600 mt-3 max-w-2xl mx-auto">
              <InlineEditableTextWithNav 
                p={p} 
                field="cakes_subtitle" 
                value="Celebrate with our handcrafted cakes made from the finest ingredients"
                onNavigate={handleNavigateAfterEdit}
                navPage="cakes"
              />
            </p>
            <div className="w-24 h-1 bg-gradient-to-r from-rose-600 to-rose-400 mx-auto mt-4 rounded-full" />
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {bakeryCollection.filter(d => d.category === "Cakes").map((item) => (
              <div 
                key={item.id} 
                className="group cursor-pointer" 
                onClick={() => setSelectedItem(item)}
              >
                <div className="relative overflow-hidden rounded-2xl bg-white shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-3">
                  <div className="relative aspect-square overflow-hidden bg-rose-50">
                    <img src={item.image} alt={item.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                    {item.badge && (
                      <div className="absolute top-3 left-3 px-3 py-1 rounded-full text-white text-xs font-bold shadow-lg" style={{ backgroundColor: item.badgeColor }}>
                        {item.badge}
                      </div>
                    )}
                  </div>
                  <div className="p-4 text-center">
                    <h3 className="font-semibold text-gray-800">
                      <InlineEditableTextWithNav 
                        p={p} 
                        field={item.nameKey} 
                        value={item.name}
                        onNavigate={handleNavigateAfterEdit}
                        navPage="cakes"
                      />
                    </h3>
                    <div className="mt-2 flex items-center justify-center gap-2">
                      <span className="text-xl font-bold text-rose-700">₹<InlineEditableTextWithNav 
                        p={p} 
                        field={item.priceKey} 
                        value={item.price.toString()}
                        onNavigate={handleNavigateAfterEdit}
                        navPage="cakes"
                      /></span>
                      {item.originalPrice > item.price && (
                        <span className="text-sm text-gray-400 line-through">₹<InlineEditableTextWithNav 
                          p={p} 
                          field={item.originalPriceKey} 
                          value={item.originalPrice.toString()}
                          onNavigate={handleNavigateAfterEdit}
                          navPage="cakes"
                        /></span>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mt-1">{item.serves}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
      
      {/* ==================== PASTRIES PAGE ==================== */}
      {activePage === "pastries" && (
        <section className="w-full px-4 md:px-8 py-12">
          <div className="text-center mb-12">
            <span className="text-amber-700 text-sm tracking-[0.2em] uppercase">
              <InlineEditableTextWithNav 
                p={p} 
                field="pastries_badge" 
                value="Pastry Collection"
                onNavigate={handleNavigateAfterEdit}
                navPage="pastries"
              />
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-amber-900 mt-2">
              <InlineEditableTextWithNav 
                p={p} 
                field="pastries_title" 
                value="Artisan Pastries"
                onNavigate={handleNavigateAfterEdit}
                navPage="pastries"
              />
            </h2>
            <p className="text-gray-600 mt-3 max-w-2xl mx-auto">
              <InlineEditableTextWithNav 
                p={p} 
                field="pastries_subtitle" 
                value="Buttery, flaky pastries made fresh daily"
                onNavigate={handleNavigateAfterEdit}
                navPage="pastries"
              />
            </p>
            <div className="w-24 h-1 bg-gradient-to-r from-amber-600 to-amber-400 mx-auto mt-4 rounded-full" />
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {bakeryCollection.filter(d => d.category === "Pastries" || d.category === "Desserts").map((item) => (
              <div 
                key={item.id} 
                className="group cursor-pointer" 
                onClick={() => setSelectedItem(item)}
              >
                <div className="relative overflow-hidden rounded-2xl bg-white shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-3">
                  <div className="relative aspect-square overflow-hidden bg-amber-50">
                    <img src={item.image} alt={item.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                  </div>
                  <div className="p-4 text-center">
                    <h3 className="font-semibold text-gray-800">
                      <InlineEditableTextWithNav 
                        p={p} 
                        field={item.nameKey} 
                        value={item.name}
                        onNavigate={handleNavigateAfterEdit}
                        navPage="pastries"
                      />
                    </h3>
                    <div className="mt-2 flex items-center justify-center gap-2">
                      <span className="text-xl font-bold text-amber-700">₹<InlineEditableTextWithNav 
                        p={p} 
                        field={item.priceKey} 
                        value={item.price.toString()}
                        onNavigate={handleNavigateAfterEdit}
                        navPage="pastries"
                      /></span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">{item.serves}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
      
      {/* ==================== BREADS PAGE ==================== */}
      {activePage === "breads" && (
        <section className="w-full px-4 md:px-8 py-12">
          <div className="text-center mb-12">
            <span className="text-purple-700 text-sm tracking-[0.2em] uppercase">
              <InlineEditableTextWithNav 
                p={p} 
                field="breads_badge" 
                value="Bread Collection"
                onNavigate={handleNavigateAfterEdit}
                navPage="breads"
              />
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-purple-900 mt-2">
              <InlineEditableTextWithNav 
                p={p} 
                field="breads_title" 
                value="Artisan Breads"
                onNavigate={handleNavigateAfterEdit}
                navPage="breads"
              />
            </h2>
            <p className="text-gray-600 mt-3 max-w-2xl mx-auto">
              <InlineEditableTextWithNav 
                p={p} 
                field="breads_subtitle" 
                value="Crusty, artisan breads baked to perfection"
                onNavigate={handleNavigateAfterEdit}
                navPage="breads"
              />
            </p>
            <div className="w-24 h-1 bg-gradient-to-r from-purple-600 to-purple-400 mx-auto mt-4 rounded-full" />
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {bakeryCollection.filter(d => d.category === "Breads").map((item) => (
              <div 
                key={item.id} 
                className="group cursor-pointer" 
                onClick={() => setSelectedItem(item)}
              >
                <div className="relative overflow-hidden rounded-2xl bg-white shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-3">
                  <div className="relative aspect-square overflow-hidden bg-purple-50">
                    <img src={item.image} alt={item.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                  </div>
                  <div className="p-4 text-center">
                    <h3 className="font-semibold text-gray-800">
                      <InlineEditableTextWithNav 
                        p={p} 
                        field={item.nameKey} 
                        value={item.name}
                        onNavigate={handleNavigateAfterEdit}
                        navPage="breads"
                      />
                    </h3>
                    <div className="mt-2 flex items-center justify-center gap-2">
                      <span className="text-xl font-bold text-purple-700">₹<InlineEditableTextWithNav 
                        p={p} 
                        field={item.priceKey} 
                        value={item.price.toString()}
                        onNavigate={handleNavigateAfterEdit}
                        navPage="breads"
                      /></span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">{item.serves}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
      
      {/* ==================== RECIPES PAGE ==================== */}
      {activePage === "recipes" && (
        <section className="w-full px-4 md:px-8 py-12">
          <div className="text-center mb-12">
            <span className="text-amber-700 text-sm tracking-[0.2em] uppercase">
              <InlineEditableTextWithNav 
                p={p} 
                field="recipes_page_badge" 
                value="Baking Tips"
                onNavigate={handleNavigateAfterEdit}
                navPage="recipes"
              />
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-amber-900 mt-2">
              <InlineEditableTextWithNav 
                p={p} 
                field="recipes_page_title" 
                value="Recipes & Techniques"
                onNavigate={handleNavigateAfterEdit}
                navPage="recipes"
              />
            </h2>
            <p className="text-gray-600 mt-3 max-w-2xl mx-auto">
              <InlineEditableTextWithNav 
                p={p} 
                field="recipes_page_subtitle" 
                value="Master the art of baking with our expert guides"
                onNavigate={handleNavigateAfterEdit}
                navPage="recipes"
              />
            </p>
            <div className="w-24 h-1 bg-gradient-to-r from-amber-600 to-amber-400 mx-auto mt-4 rounded-full" />
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {recipes.map((recipe) => (
              <div 
                key={recipe.id} 
                className="group bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 cursor-pointer"
                onClick={(e) => openRecipeModal(recipe, e)}
              >
                <div className="h-56 overflow-hidden">
                  <img src={recipe.image} alt={recipe.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-xs bg-amber-100 text-amber-800 px-3 py-1 rounded-full font-semibold">
                      {recipe.difficulty}
                    </span>
                    <span className="text-xs text-gray-500">⏱ {recipe.time}</span>
                  </div>
                  <h3 className="text-xl font-bold text-amber-900 mb-2">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field={recipe.titleKey} 
                      value={recipe.title}
                      onNavigate={handleNavigateAfterEdit}
                      navPage="recipes"
                    />
                  </h3>
                  <p className="text-gray-600">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field={recipe.descKey} 
                      value={recipe.desc}
                      onNavigate={handleNavigateAfterEdit}
                      navPage="recipes"
                    />
                  </p>
                  <div className="mt-4 flex flex-wrap gap-1">
                    {recipe.ingredients.slice(0, 3).map((ing, idx) => (
                      <span key={idx} className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">
                        {ing}
                      </span>
                    ))}
                    {recipe.ingredients.length > 3 && (
                      <span className="text-xs text-gray-400">+{recipe.ingredients.length - 3} more</span>
                    )}
                  </div>
                  <div className="mt-4 text-amber-700 font-semibold flex items-center gap-1 group-hover:gap-2 transition-all">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field="view_full_recipe" 
                      value="View Full Recipe"
                      onNavigate={handleNavigateAfterEdit}
                      navPage="recipes"
                    />
                    <span>→</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
      
      {/* ==================== JOURNAL PAGE ==================== */}
      {activePage === "journal" && (
        <section className="w-full px-4 md:px-8 py-12">
          <div className="text-center mb-12">
            <span className="text-amber-700 text-sm tracking-[0.2em] uppercase">
              <InlineEditableTextWithNav 
                p={p} 
                field="journal_badge" 
                value="Our Journal"
                onNavigate={handleNavigateAfterEdit}
                navPage="journal"
              />
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-amber-900 mt-2">
              <InlineEditableTextWithNav 
                p={p} 
                field="journal_title" 
                value="Baking Stories"
                onNavigate={handleNavigateAfterEdit}
                navPage="journal"
              />
            </h2>
            <p className="text-gray-600 mt-3 max-w-2xl mx-auto">
              <InlineEditableTextWithNav 
                p={p} 
                field="journal_subtitle" 
                value="Stories from our kitchen and baking tips"
                onNavigate={handleNavigateAfterEdit}
                navPage="journal"
              />
            </p>
            <div className="w-24 h-1 bg-gradient-to-r from-amber-600 to-amber-400 mx-auto mt-4 rounded-full" />
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {editorialPosts.map((post) => (
              <div 
                key={post.id} 
                className="group bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 cursor-pointer"
                onClick={(e) => openBlogModal(post, e)}
              >
                <div className="h-56 overflow-hidden">
                  <img src={post.image} alt={post.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                </div>
                <div className="p-5">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs text-amber-600 font-semibold">{post.category}</span>
                    <span className="text-xs text-gray-400">
                      <InlineEditableTextWithNav 
                        p={p} 
                        field={post.dateKey} 
                        value={post.date}
                        onNavigate={handleNavigateAfterEdit}
                        navPage="journal"
                      />
                    </span>
                  </div>
                  <h3 className="text-xl font-semibold text-amber-900 mb-2">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field={post.titleKey} 
                      value={post.title}
                      onNavigate={handleNavigateAfterEdit}
                      navPage="journal"
                    />
                  </h3>
                  <p className="text-gray-600 text-sm line-clamp-2">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field={post.descKey} 
                      value={post.desc}
                      onNavigate={handleNavigateAfterEdit}
                      navPage="journal"
                    />
                  </p>
                  <div className="mt-4 flex items-center justify-between">
                    <span className="text-xs text-gray-400">
                      <InlineEditableTextWithNav 
                        p={p} 
                        field="by_prefix" 
                        value="By"
                        onNavigate={handleNavigateAfterEdit}
                        navPage="journal"
                      />{' '}
                      <span className="font-semibold text-amber-800">
                        <InlineEditableTextWithNav 
                          p={p} 
                          field={post.authorKey} 
                          value={post.author}
                          onNavigate={handleNavigateAfterEdit}
                          navPage="journal"
                        />
                      </span>
                    </span>
                    <button 
                      onClick={(e) => openBlogModal(post, e)}
                      className="text-amber-700 text-sm font-semibold flex items-center gap-1 group-hover:gap-2 transition-all"
                    >
                      <InlineEditableTextWithNav 
                        p={p} 
                        field="read_more_button" 
                        value="Read More"
                        onNavigate={handleNavigateAfterEdit}
                        navPage="journal"
                      />
                      <span>→</span>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
      
      {/* ==================== ABOUT PAGE ==================== */}
      {activePage === "about" && (
        <section className="w-full px-4 md:px-8 py-12 md:py-20">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-amber-700 text-sm tracking-wider uppercase">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="about_badge" 
                  value="Our Story"
                  onNavigate={handleNavigateAfterEdit}
                  navPage="about"
                />
              </span>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-amber-900 mt-3">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="about_title" 
                  value="A Love for Baking"
                  onNavigate={handleNavigateAfterEdit}
                  navPage="about"
                />
              </h2>
              <div className="w-16 h-1 bg-amber-600 mt-4 mb-6" />
              <p className="text-gray-600 leading-relaxed mb-4">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="about_para1" 
                  value="Golden Harvest Bakery was born from a passion for creating beautiful, delicious baked goods. Every recipe is carefully crafted and tested to ensure perfection in every bite."
                  onNavigate={handleNavigateAfterEdit}
                  navPage="about"
                />
              </p>
              <p className="text-gray-600 leading-relaxed mb-6">
                <InlineEditableTextWithNav 
                  p={p} 
                  field="about_para2" 
                  value="We use only the finest ingredients—European butter, single-origin chocolate, and organic flour—to create pastries and breads that transport you to a French patisserie. Quality is our commitment, and taste is our passion."
                  onNavigate={handleNavigateAfterEdit}
                  navPage="about"
                />
              </p>
              <div className="flex flex-wrap gap-6">
                <div>
                  <p className="text-3xl font-bold text-amber-700">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field="about_stat1_value" 
                      value="50+"
                      onNavigate={handleNavigateAfterEdit}
                      navPage="about"
                    />
                  </p>
                  <p className="text-sm text-gray-500">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field="about_stat1_label" 
                      value="Recipes"
                      onNavigate={handleNavigateAfterEdit}
                      navPage="about"
                    />
                  </p>
                </div>
                <div>
                  <p className="text-3xl font-bold text-amber-700">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field="about_stat2_value" 
                      value="10+"
                      onNavigate={handleNavigateAfterEdit}
                      navPage="about"
                    />
                  </p>
                  <p className="text-sm text-gray-500">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field="about_stat2_label" 
                      value="Years of Experience"
                      onNavigate={handleNavigateAfterEdit}
                      navPage="about"
                    />
                  </p>
                </div>
                <div>
                  <p className="text-3xl font-bold text-amber-700">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field="about_stat3_value" 
                      value="5k+"
                      onNavigate={handleNavigateAfterEdit}
                      navPage="about"
                    />
                  </p>
                  <p className="text-sm text-gray-500">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field="about_stat3_label" 
                      value="Happy Customers"
                      onNavigate={handleNavigateAfterEdit}
                      navPage="about"
                    />
                  </p>
                </div>
              </div>
            </div>
            <div className="rounded-2xl overflow-hidden shadow-2xl">
              <img 
                src="https://s3.us-east-1.amazonaws.com/dukanbuddy-normal/blogs/1dfc2629-f789-4ef0-bb07-dc4851ac683b_b1.jpg" 
                alt="About Golden Harvest" 
                className="w-full h-full object-cover" 
              />
            </div>
          </div>
        </section>
      )}
      
      {/* ==================== PRODUCT TABS ==================== */}
      <section className="w-full px-4 md:px-8 mt-8">
        <div className="flex justify-center gap-2 flex-wrap">
          {[
            ["products", "🎂 Shop Bakery"],
            ["services", "🛠 Services"],
            ["details", "📖 About Store"]
          ].map(([key, label]) => (
            <button 
              key={key} 
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (!(p.previewEditMode && p.activeEditField)) {
                  p.setActiveTab(key);
                }
              }} 
              className={`px-5 md:px-7 py-2.5 rounded-full text-sm font-medium transition-all duration-300 ${p.activeTab === key ? "bg-amber-700 text-white shadow-lg" : "text-gray-500 hover:text-amber-700"}`}
            >
              <InlineEditableTextWithNav 
                p={p} 
                field={`tab_${key}_label`} 
                value={label}
                onNavigate={handleNavigateAfterEdit}
                navPage={activePage}
              />
            </button>
          ))}
        </div>
      </section>
      
      {/* Main Content */}
      <section className="flex-1 w-full px-4 md:px-8 py-8 md:py-12">
        <TabContent p={p} />
      </section>
      
      {/* Newsletter */}
      <section className="bg-gradient-to-r from-amber-800 to-amber-900 py-16 mt-8 w-full">
        <div className="max-w-3xl mx-auto text-center px-4">
          <span className="text-amber-300 text-sm tracking-wider uppercase">
            <InlineEditableTextWithNav 
              p={p} 
              field="newsletter_badge" 
              value="Subscribe"
              onNavigate={handleNavigateAfterEdit}
              navPage="home"
            />
          </span>
          <h3 className="text-2xl md:text-3xl font-bold text-white mt-2">
            <InlineEditableTextWithNav 
              p={p} 
              field="newsletter_title" 
              value="Join the Baking Club"
              onNavigate={handleNavigateAfterEdit}
              navPage="home"
            />
          </h3>
          <p className="text-amber-200 text-sm mt-2">
            <InlineEditableTextWithNav 
              p={p} 
              field="newsletter_subtitle" 
              value="Get 10% off your first order + exclusive recipes"
              onNavigate={handleNavigateAfterEdit}
              navPage="home"
            />
          </p>
          <div className="mt-6 flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
            <input 
              type="email" 
              placeholder={p.storefrontText?.email_placeholder || "Your email address"} 
              className="flex-1 px-5 py-3 bg-white/10 border border-amber-400/30 rounded-full text-white placeholder-amber-300/50 focus:border-amber-400 outline-none transition" 
            />
            <button 
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (!(p.previewEditMode && p.activeEditField)) {
                  // Handle newsletter subscription
                }
              }}
              className="px-6 py-3 rounded-full bg-amber-600 text-white font-semibold hover:bg-amber-500 transition"
            >
              <InlineEditableTextWithNav 
                p={p} 
                field="subscribe_button" 
                value="Subscribe"
                onNavigate={handleNavigateAfterEdit}
                navPage="home"
              />
            </button>
          </div>
        </div>
      </section>
      
      <StoreFooter p={p} />
      
      {/* ==================== MODALS ==================== */}
      
      {/* Item Modal */}
      {selectedItem && (
        <div 
          className="fixed inset-0 bg-black/80 backdrop-blur-md z-[100] flex items-center justify-center p-4 animate-fade-in" 
          onClick={handleBackdropClick}
        >
          <div className="max-w-md w-full bg-white rounded-2xl overflow-hidden shadow-2xl animate-slide-up" onClick={(e) => e.stopPropagation()}>
            <div className="relative h-72">
              <img src={selectedItem.image} alt={selectedItem.name} className="w-full h-full object-cover" />
              <button 
                onClick={() => setSelectedItem(null)} 
                className="absolute top-3 right-3 w-10 h-10 rounded-full bg-black/50 text-white hover:bg-amber-600 transition flex items-center justify-center text-xl"
              >
                ✕
              </button>
              {selectedItem.badge && (
                <div className="absolute top-3 left-3 px-3 py-1 rounded-full text-white text-xs font-bold shadow-lg" style={{ backgroundColor: selectedItem.badgeColor }}>
                  {selectedItem.badge}
                </div>
              )}
              {selectedItem.isVeg && (
                <div className="absolute bottom-3 right-3 bg-green-500 text-white text-xs font-bold px-2 py-1 rounded-full">
                  🟢 VEG
                </div>
              )}
            </div>
            <div className="p-6">
              <h3 className="text-2xl font-bold text-amber-900">
                <InlineEditableTextWithNav 
                  p={p} 
                  field={selectedItem.nameKey} 
                  value={selectedItem.name}
                  onNavigate={handleNavigateAfterEdit}
                  navPage={activePage}
                  isInModal={true}
                />
              </h3>
              <p className="text-gray-600 text-sm mt-2">
                <InlineEditableTextWithNav 
                  p={p} 
                  field={selectedItem.descriptionKey} 
                  value={selectedItem.description}
                  onNavigate={handleNavigateAfterEdit}
                  navPage={activePage}
                  isInModal={true}
                />
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                <span className="text-xs bg-amber-100 text-amber-800 px-2 py-1 rounded-full">
                  {selectedItem.category}
                </span>
                <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full">
                  {selectedItem.serves}
                </span>
              </div>
              <div className="mt-4 flex items-center gap-3">
                <span className="text-3xl font-bold text-amber-700">
                  ₹<InlineEditableTextWithNav 
                    p={p} 
                    field={selectedItem.priceKey} 
                    value={selectedItem.price.toString()}
                    onNavigate={handleNavigateAfterEdit}
                    navPage={activePage}
                    isInModal={true}
                  />
                </span>
                {selectedItem.originalPrice > selectedItem.price && (
                  <span className="text-sm text-gray-400 line-through">
                    ₹<InlineEditableTextWithNav 
                      p={p} 
                      field={selectedItem.originalPriceKey} 
                      value={selectedItem.originalPrice.toString()}
                      onNavigate={handleNavigateAfterEdit}
                      navPage={activePage}
                      isInModal={true}
                    />
                  </span>
                )}
              </div>
              <div className="mt-4">
                <p className="text-xs text-gray-500 font-semibold">Ingredients:</p>
                <div className="flex flex-wrap gap-1 mt-1">
                  {selectedItem.ingredients?.map((ing, idx) => (
                    <span key={idx} className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">
                      {ing}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Recipe Modal */}
      {selectedRecipe && (
        <div 
          className="fixed inset-0 bg-black/80 backdrop-blur-md z-[100] flex items-center justify-center p-4 animate-fade-in" 
          onClick={handleBackdropClick}
        >
          <div className="max-w-2xl w-full bg-white rounded-2xl overflow-hidden shadow-2xl animate-slide-up max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="relative h-64">
              <img src={selectedRecipe.image} alt={selectedRecipe.title} className="w-full h-full object-cover" />
              <button 
                onClick={closeRecipeModal} 
                className="absolute top-3 right-3 w-10 h-10 rounded-full bg-black/50 text-white hover:bg-amber-600 transition flex items-center justify-center text-xl"
              >
                ✕
              </button>
              <div className="absolute bottom-4 left-4 flex gap-2">
                <span className="bg-amber-800/80 text-amber-200 px-3 py-1 rounded-full text-xs font-semibold backdrop-blur-sm">
                  {selectedRecipe.difficulty}
                </span>
                <span className="bg-amber-800/80 text-amber-200 px-3 py-1 rounded-full text-xs font-semibold backdrop-blur-sm">
                  ⏱ {selectedRecipe.time}
                </span>
              </div>
            </div>
            <div className="p-6">
              <h2 className="text-2xl font-bold text-amber-900 mb-4">
                <InlineEditableTextWithNav 
                  p={p} 
                  field={selectedRecipe.titleKey} 
                  value={selectedRecipe.title}
                  onNavigate={handleNavigateAfterEdit}
                  navPage="recipes"
                  isInModal={true}
                />
              </h2>
              <div className="mb-6">
                <p className="font-semibold text-amber-800 text-sm">Ingredients:</p>
                <div className="flex flex-wrap gap-2 mt-2">
                  {selectedRecipe.ingredients?.map((ing, idx) => (
                    <span key={idx} className="text-sm bg-amber-50 text-gray-700 px-3 py-1 rounded-full">
                      {ing}
                    </span>
                  ))}
                </div>
              </div>
              <div className="space-y-4">
                <p className="font-semibold text-amber-800 text-sm">Instructions:</p>
                {selectedRecipe.steps?.map((step, idx) => (
                  <div key={idx} className="flex items-start gap-3 p-3 bg-amber-50 rounded-xl">
                    <span className="w-8 h-8 rounded-full bg-amber-700 text-white flex items-center justify-center font-bold text-sm flex-shrink-0">
                      {idx + 1}
                    </span>
                    <p className="text-gray-700 text-sm">{step}</p>
                  </div>
                ))}
              </div>
              <div className="mt-6 flex justify-end">
                <button 
                  onClick={closeRecipeModal} 
                  className="px-6 py-2 rounded-full bg-amber-700 text-white hover:bg-amber-800 transition"
                >
                  <InlineEditableTextWithNav 
                    p={p} 
                    field="close_button" 
                    value="Close"
                    onNavigate={handleNavigateAfterEdit}
                    navPage="recipes"
                    isInModal={true}
                  />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Blog Modal */}
      {selectedPost && (
        <div 
          className="fixed inset-0 bg-black/80 backdrop-blur-md z-[100] flex items-center justify-center p-4 animate-fade-in" 
          onClick={handleBackdropClick}
        >
          <div className="max-w-2xl w-full bg-white rounded-2xl overflow-hidden shadow-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 z-10 bg-white border-b border-gray-100 p-4 flex justify-between items-center">
              <h3 className="text-lg font-semibold text-amber-900">Baking Stories</h3>
              <button 
                onClick={closeBlogModal} 
                className="w-10 h-10 rounded-full bg-gray-100 text-gray-600 hover:bg-amber-600 hover:text-white transition flex items-center justify-center text-xl"
              >
                ✕
              </button>
            </div>
            <div className="p-6">
              <div className="mb-6">
                <img src={selectedPost.image} alt={selectedPost.title} className="w-full h-64 object-cover rounded-xl" />
              </div>
              <div className="mb-3">
                <span className="inline-block px-3 py-1 bg-amber-100 text-amber-800 text-xs rounded-full font-semibold">
                  {selectedPost.category}
                </span>
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-amber-900 mb-2">
                <InlineEditableTextWithNav 
                  p={p} 
                  field={selectedPost.titleKey} 
                  value={selectedPost.title}
                  onNavigate={handleNavigateAfterEdit}
                  navPage="journal"
                  isInModal={true}
                />
              </h2>
              <div className="flex flex-wrap items-center gap-3 text-sm text-gray-500 border-b border-gray-100 pb-4 mb-4">
                <span>
                  <InlineEditableTextWithNav 
                    p={p} 
                    field={selectedPost.dateKey} 
                    value={selectedPost.date}
                    onNavigate={handleNavigateAfterEdit}
                    navPage="journal"
                    isInModal={true}
                  />
                </span>
                <span>•</span>
                <span>
                  <InlineEditableTextWithNav 
                    p={p} 
                    field="by_prefix" 
                    value="By"
                    onNavigate={handleNavigateAfterEdit}
                    navPage="journal"
                    isInModal={true}
                  />{' '}
                  <span className="font-semibold text-amber-900">
                    <InlineEditableTextWithNav 
                      p={p} 
                      field={selectedPost.authorKey} 
                      value={selectedPost.author}
                      onNavigate={handleNavigateAfterEdit}
                      navPage="journal"
                      isInModal={true}
                    />
                  </span>
                </span>
              </div>
              <div className="prose prose-lg max-w-none">
                <p className="text-gray-600 leading-relaxed whitespace-pre-wrap">
                  <InlineEditableTextWithNav 
                    p={p} 
                    field={selectedPost.fullContentKey} 
                    value={selectedPost.fullContent}
                    onNavigate={handleNavigateAfterEdit}
                    navPage="journal"
                    isInModal={true}
                  />
                </p>
              </div>
              <div className="mt-8 pt-4 border-t border-gray-100 flex justify-end">
                <button 
                  onClick={closeBlogModal} 
                  className="px-6 py-2 rounded-full bg-amber-700 text-white hover:bg-amber-800 transition"
                >
                  <InlineEditableTextWithNav 
                    p={p} 
                    field="close_button" 
                    value="Close"
                    onNavigate={handleNavigateAfterEdit}
                    navPage="journal"
                    isInModal={true}
                  />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      <style jsx>{`
        @keyframes marquee { 0% { transform: translateX(100%); } 100% { transform: translateX(-100%); } }
        .animate-marquee { animation: marquee 20s linear infinite; }
        
        @keyframes float-wheat { 0%,100% { transform: translateY(0px) translateX(0px); opacity: 0.1; } 50% { transform: translateY(-60px) translateX(25px); opacity: 0.4; } }
        .animate-float-wheat { animation: float-wheat 6s ease-in-out infinite; }
        
        @keyframes word-pop { 0% { opacity: 0; transform: scale(0.5) rotate(-8deg); } 60% { transform: scale(1.08) rotate(2deg); } 100% { opacity: 1; transform: scale(1) rotate(0); } }
        .animate-word-pop { animation: word-pop 0.6s cubic-bezier(0.34,1.2,0.64,1) forwards; opacity: 0; }
        
        @keyframes slide-right { from { opacity: 0; transform: translateX(-40px); } to { opacity: 1; transform: translateX(0); } }
        .animate-slide-right { animation: slide-right 0.7s ease-out forwards; opacity: 0; animation-fill-mode: forwards; }
        
        @keyframes bounce-in { 0% { opacity: 0; transform: scale(0.3); } 50% { transform: scale(1.05); } 70% { transform: scale(0.95); } 100% { opacity: 1; transform: scale(1); } }
        .animate-bounce-in { animation: bounce-in 0.7s cubic-bezier(0.34,1.2,0.64,1) forwards; opacity: 0; }
        
        @keyframes card-fly { 0% { opacity: 0; transform: translateY(40px) scale(0.9); } 60% { transform: translateY(-5px) scale(1.02); } 100% { opacity: 1; transform: translateY(0) scale(1); } }
        .animate-card-fly { animation: card-fly 0.6s cubic-bezier(0.34,1.2,0.64,1) forwards; opacity: 0; }
        
        @keyframes scroll-dot { 0% { transform: translateY(0); opacity: 1; } 100% { transform: translateY(12px); opacity: 0; } }
        .animate-scroll-dot { animation: scroll-dot 1.5s ease-in-out infinite; }
        
        @keyframes slide-up { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
        .animate-slide-up { animation: slide-up 0.5s ease-out forwards; opacity: 0; animation-fill-mode: forwards; }
        
        @keyframes fade-in { from { opacity: 0; } to { opacity: 1; } }
        .animate-fade-in { animation: fade-in 0.3s ease-out forwards; }
        
        @keyframes bounce { 0%,100% { transform: translateX(-50%) translateY(0); } 50% { transform: translateX(-50%) translateY(-8px); } }
        .animate-bounce-slow { animation: bounce 2s ease-in-out infinite; }
        
        @keyframes slide-up-modal { from { opacity: 0; transform: translateY(30px) scale(0.95); } to { opacity: 1; transform: translateY(0) scale(1); } }
        .animate-slide-up { animation: slide-up-modal 0.4s ease-out forwards; }
        
        .line-clamp-1 { display: -webkit-box; -webkit-line-clamp: 1; -webkit-box-orient: vertical; overflow: hidden; }
        .line-clamp-2 { display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
      `}</style>
    </div>
  );
}